import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconCopy,
  IconDots,
  IconPencil,
  IconShare,
  IconTrash,
} from "@tabler/icons-react";
import styles from "./ReportHeader.module.css";

export const DESCRIPTION_MAX_LENGTH = 500;

export interface ReportHeaderProps {
  /** Display name (custom name, else the catalog name). */
  name: string;
  /** Display description (custom, else the catalog's). */
  description: string;
  /** Catalog name, shown as the name input's placeholder. */
  defaultName: string;
  /** Open the name / description editor immediately (e.g. right after
   *  "Add again" or Duplicate, so the user can tell the copies apart). */
  autoEdit?: boolean;
  /** Called once `autoEdit` has been consumed. */
  onAutoEditHandled?: () => void;
  /** Persist the edited name / description. Resolve true on success. */
  onSaveMeta: (name: string, description: string) => Promise<boolean>;
  onDuplicate: () => void;
  onRemove: () => void;
  /** Opens the Share modal; omitted (no menu item) when the caller can't
   *  share reports. */
  onShare?: () => void;
  /** On a copy someone shared with the caller, e.g. "Shared by Alice ·
   *  Synced with their changes". */
  sharedNote?: string | null;
  /** Menu label for duplicate ("Duplicate with this view" for v2 reports). */
  duplicateLabel?: string;
  /** Right-side slot before the "…" menu (e.g. Unsaved changes · Save view). */
  actions?: React.ReactNode;
}

/**
 * Report title + description with an inline editor (pencil, or the "…" menu's
 * "Edit name & description"). Enter saves, Esc cancels. The "…" menu also
 * offers Duplicate, Share (when the caller may share) and Remove from list
 * (the caller confirms the removal).
 */
export default function ReportHeader({
  name,
  description,
  defaultName,
  autoEdit = false,
  onAutoEditHandled,
  onSaveMeta,
  onDuplicate,
  onRemove,
  onShare,
  sharedNote,
  duplicateLabel = "Duplicate",
  actions,
}: ReportHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftDescription, setDraftDescription] = useState(description);

  const beginEdit = () => {
    setDraftName(name);
    setDraftDescription(description);
    setEditing(true);
  };

  // Open the editor when asked to. The caller clears `autoEdit` in
  // onAutoEditHandled, so later name / description changes don't re-run it.
  useEffect(() => {
    if (!autoEdit) return;
    setDraftName(name);
    setDraftDescription(description);
    setEditing(true);
    onAutoEditHandled?.();
  }, [autoEdit, name, description, onAutoEditHandled]);

  const cancel = () => setEditing(false);

  const save = async () => {
    setSaving(true);
    try {
      const ok = await onSaveMeta(draftName, draftDescription);
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <Box className={styles.header}>
      {editing ? (
        <Stack gap="xs" className={styles.editBlock}>
          <TextInput
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            placeholder={defaultName || "Report name"}
            aria-label="Report name"
            classNames={{ input: styles.nameInput }}
          />
          <TextInput
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            maxLength={DESCRIPTION_MAX_LENGTH}
            placeholder="Description, e.g. Last month · front desk staff"
            aria-label="Description"
          />
          <Group gap="xs">
            <Button size="xs" color="teal" onClick={save} loading={saving}>
              Save
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={cancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Box className={styles.titleBlock}>
          <Group gap="xs" wrap="nowrap" miw={0}>
            <Title order={2} className={styles.title} title={name}>
              {name}
            </Title>
            <Tooltip label="Edit name & description" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                className={styles.pencil}
                aria-label="Edit name and description"
                onClick={beginEdit}
              >
                <IconPencil size={14} aria-hidden />
              </ActionIcon>
            </Tooltip>
          </Group>
          {description && (
            <Text className={styles.description}>{description}</Text>
          )}
          {sharedNote && (
            <Text size="xs" c="dimmed" mt={2}>
              {sharedNote}
            </Text>
          )}
        </Box>
      )}

      <Box className={styles.actions}>
        {actions}
        <Menu position="bottom-end" shadow="md" radius="var(--radius-lg)">
          <Menu.Target>
            <ActionIcon
              variant="default"
              size="lg"
              className={styles.more}
              aria-label="More options"
            >
              <IconDots size={17} aria-hidden />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconPencil size={15} aria-hidden />}
              onClick={beginEdit}
            >
              Edit name &amp; description
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCopy size={15} aria-hidden />}
              onClick={onDuplicate}
            >
              {duplicateLabel}
            </Menu.Item>
            {onShare && (
              <Menu.Item
                leftSection={<IconShare size={15} aria-hidden />}
                onClick={onShare}
              >
                Share
              </Menu.Item>
            )}
            <Menu.Divider />
            <Menu.Item
              className={styles.danger}
              leftSection={<IconTrash size={15} aria-hidden />}
              onClick={onRemove}
            >
              Remove from list
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Box>
  );
}
